import { useField } from 'formik';
import { useState } from 'react';

import {
  FormikAutocompleteField,
  IFormikAutocompleteFieldProps,
} from 'ui/common/components/FormFields/FormikAutocompleteField';
import { TAutocompleteFieldFeature } from 'ui/common/components/FormFields/FormikAutocompleteField/types';

const DEFAULT_MIN_NUM_CHARACTERS_TYPED_TO_SHOW_OPTIONS = 2;

export interface IFormikAutocompleteSearchFieldProps<
  T,
  Multiple extends TAutocompleteFieldFeature = undefined,
  DisableClearable extends TAutocompleteFieldFeature = undefined,
  FreeSolo extends TAutocompleteFieldFeature = undefined,
> extends IFormikAutocompleteFieldProps<T, Multiple, DisableClearable, FreeSolo> {
  // When at least this many characters are entered, matching options will be shown. If fewer
  // characters are currently entered, the area containing the options will not be visible.
  // Defaults to DEFAULT_MIN_NUM_CHARACTERS_TYPED_TO_SHOW_OPTIONS.
  numberOfCharactersRequiredToSeeOptions?: number;

  // The default behavior to determine whether an option matches what the user has inputted is to
  // check if the option label starts with the input (case-insensitive). For example, the
  // "Teamsters" option would show up if the user entered "Tea" or "tea", but not if they entered
  // "sters".
  // This prop can be used to override that behavior - e.g. to do case-sensitive matching, or match
  // against the value instead of the label, or match anywhere in the string.
  optionMatches?: (value: string) => (option: T, optionLabel: string) => boolean;
}

/**
 * This wraps the FormikAutocompleteField component to add the following functionality:
 * - only display options after a certain number of characters are entered
 * - only display the options that match the entered characters
 *
 * Note that this functionality does not securely hide non-matching options from the user, as they
 * can still be discovered by inspecting the page regardless of the number of characters entered.
 *
 * TODO (EC-1367) this was written for a use case that we intend to remove in the first half of 2023.
 * When that time comes, delete this component or make it a common component and add stories if there
 * are other use cases.
 */
export const FormikAutocompleteSearchField = <
  T,
  Multiple extends TAutocompleteFieldFeature = undefined,
  DisableClearable extends TAutocompleteFieldFeature = undefined,
  FreeSolo extends TAutocompleteFieldFeature = undefined,
>(
  props: IFormikAutocompleteSearchFieldProps<T, Multiple, DisableClearable, FreeSolo>
) => {
  const {
    numberOfCharactersRequiredToSeeOptions = DEFAULT_MIN_NUM_CHARACTERS_TYPED_TO_SHOW_OPTIONS,
    optionMatches = (value: string) => {
      return (_, optionLabel: string) => {
        return optionLabel.toLowerCase().startsWith(value.toLowerCase());
      };
    },
    onInputChange,
    name,
    ...otherProps
  } = props;

  // This is the value (not the label) of the option selected, or null.
  const [selectedOption] = useField<T>(name);

  // This is the text that has been typed into the field. It is reset to '' when an option is
  // selected.
  const [inputValue, setInputValue] = useState('');

  const filterOptions = (
    options: T[],
    state: {
      getOptionLabel: (option: T) => string;
    }
  ): T[] => {
    // If an option has been selected, only show that option
    if (selectedOption.value !== null) {
      return [selectedOption.value];
    }

    // If at least numberOfCharactersToEnterToSeeOptions characters have been entered, show options
    // that match the entered characters.
    if (inputValue.length >= numberOfCharactersRequiredToSeeOptions) {
      const includeOption = optionMatches(inputValue);
      return options.filter((option) => includeOption(option, state.getOptionLabel(option)));
    }

    // If too few characters are entered, don't show any options.
    return [];
  };

  return (
    <FormikAutocompleteField
      filterOptions={filterOptions}
      // Setting `freeSolo` when too few characters have been entered prevents the options area
      // from showing. Without this, the component displays "No options", which may be confusing.
      freeSolo={
        inputValue.length < numberOfCharactersRequiredToSeeOptions && selectedOption.value === null
      }
      onInputChange={(event, value: string, reason) => {
        setInputValue(value);
        onInputChange?.(event, value, reason);
      }}
      name={name}
      {...otherProps}
    />
  );
};
